import { createSlice } from '@reduxjs/toolkit';
import {
  complaintTypeActions,
  complaintDepartmentActions,
  complaintPriorityActions,
  complaintWorkflowActions,
  complaintStatusActions,
  getDepartmentTree,
  getWorkflowSteps,
  getFormFields,
  saveFormFields,
  deleteFormField,
  saveWorkflowSteps,
  deleteWorkflowSteps,
} from './actions'; 

const createResourceState = () => ({
  data: [],
  selected: null,
  pagination: null,
  isLoading: false,
  isMutating: false,
  error: null,
});

const initialState = {
  types: createResourceState(),
  departments: createResourceState(),
  priorities: createResourceState(),
  workflows: createResourceState(),
  statuses: createResourceState(),
  formFields: createResourceState(),
  departmentTree: [],
  workflowSteps: {},
  isLoadingDepartmentTree: false,
  isLoadingWorkflowSteps: false,
  error: null,
};

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return { data: payload, pagination: null };
  const body = payload?.data ?? payload;
  if (Array.isArray(body)) {
    return { data: body, pagination: payload?.pagination ?? null };
  }
  return {
    data: body?.results ?? body?.items ?? [],
    pagination: body?.pagination ?? payload?.pagination ?? null,
  };
};

const unwrapEntity = (payload) => payload?.data ?? payload;

const entityId = (entity) => entity?.id ?? entity?._id;

const upsert = (items, entity) => {
  if (!entity || typeof entity !== 'object') return items;
  const id = entityId(entity);
  if (id === undefined || id === null) return items;
  const index = items.findIndex((item) => entityId(item) === id);
  if (index < 0) return [entity, ...items];
  const next = [...items];
  next[index] = entity;
  return next;
};

const errorMessage = (action) =>
  action.payload?.message ?? action.error?.message ?? 'Something went wrong';

const registerResource = (builder, key, actions) => {
  builder
    .addCase(actions.list.pending, (state) => {
      state[key].isLoading = true;
      state[key].error = null;
    })
    .addCase(actions.list.fulfilled, (state, action) => {
      const result = unwrapList(action.payload);
      state[key].isLoading = false;
      state[key].data = result.data;
      state[key].pagination = result.pagination;
    })
    .addCase(actions.list.rejected, (state, action) => {
      state[key].isLoading = false;
      state[key].error = errorMessage(action);
    })
    .addCase(actions.getById.pending, (state) => {
      state[key].isLoading = true;
      state[key].error = null;
    })
    .addCase(actions.getById.fulfilled, (state, action) => {
      state[key].isLoading = false;
      state[key].selected = unwrapEntity(action.payload);
    })
    .addCase(actions.getById.rejected, (state, action) => {
      state[key].isLoading = false;
      state[key].error = errorMessage(action);
    });

  [actions.create, actions.update].forEach((actionCreator) => {
    builder
      .addCase(actionCreator.pending, (state) => {
        state[key].isMutating = true;
        state[key].error = null;
      })
      .addCase(actionCreator.fulfilled, (state, action) => {
        const entity = unwrapEntity(action.payload);
        state[key].isMutating = false;
        state[key].selected = entity;
        state[key].data = upsert(state[key].data, entity);
      })
      .addCase(actionCreator.rejected, (state, action) => {
        state[key].isMutating = false;
        state[key].error = errorMessage(action);
      });
  });

  builder
    .addCase(actions.remove.pending, (state) => {
      state[key].isMutating = true;
      state[key].error = null;
    })
    .addCase(actions.remove.fulfilled, (state, action) => {
      const deletedId = action.meta.arg;
      state[key].isMutating = false;
      state[key].data = state[key].data.filter(
        (item) => entityId(item) !== deletedId,
      );
      if (entityId(state[key].selected) === deletedId) {
        state[key].selected = null;
      }
    })
    .addCase(actions.remove.rejected, (state, action) => {
      state[key].isMutating = false;
      state[key].error = errorMessage(action);
    });
};

const complaintSlice = createSlice({
  name: 'complaintAppComplaint',
  initialState,
  reducers: {
    clearComplaintError(state, action) {
      const resource = action.payload;
      if (resource && state[resource]?.error !== undefined) {
        state[resource].error = null;
      } else {
        state.error = null;
      }
    },
    clearSelectedComplaintResource(state, action) {
      const resource = action.payload;
      if (resource && state[resource]?.selected !== undefined) {
        state[resource].selected = null;
      }
    },
    resetComplaintState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    registerResource(builder, 'types', complaintTypeActions);
    registerResource(builder, 'departments', complaintDepartmentActions);
    registerResource(builder, 'priorities', complaintPriorityActions);
    registerResource(builder, 'workflows', complaintWorkflowActions);
    registerResource(builder, 'statuses', complaintStatusActions);

    builder
      .addCase(getDepartmentTree.pending, (state) => {
        state.isLoadingDepartmentTree = true;
        state.error = null;
      })
      .addCase(getDepartmentTree.fulfilled, (state, action) => {
        state.isLoadingDepartmentTree = false;
        const value = unwrapEntity(action.payload);
        state.departmentTree = Array.isArray(value)
          ? value
          : value?.tree ?? value?.items ?? [];
      })
      .addCase(getDepartmentTree.rejected, (state, action) => {
        state.isLoadingDepartmentTree = false;
        state.error = errorMessage(action);
      })
      .addCase(getWorkflowSteps.pending, (state) => {
        state.isLoadingWorkflowSteps = true;
        state.error = null;
      })
      .addCase(getWorkflowSteps.fulfilled, (state, action) => {
        state.isLoadingWorkflowSteps = false;
        state.workflowSteps[action.meta.arg] = unwrapEntity(action.payload);
      })
      .addCase(getWorkflowSteps.rejected, (state, action) => {
        state.isLoadingWorkflowSteps = false;
        state.error = errorMessage(action);
      })
      .addCase(saveWorkflowSteps.pending, (state) => {
        state.isLoadingWorkflowSteps = true;
        state.error = null;
      })
      .addCase(saveWorkflowSteps.fulfilled, (state, action) => {
        state.isLoadingWorkflowSteps = false;
        const result = unwrapEntity(action.payload);
        const workflowId =
          action.meta.arg?.workflowId ?? result?.workflowId ?? result?.id;
        if (workflowId !== undefined) state.workflowSteps[workflowId] = result;
      })
      .addCase(saveWorkflowSteps.rejected, (state, action) => {
        state.isLoadingWorkflowSteps = false;
        state.error = errorMessage(action);
      })
      .addCase(deleteWorkflowSteps.fulfilled, (state, action) => {
        delete state.workflowSteps[action.meta.arg];
      })
      .addCase(getFormFields.pending, (state) => {
        state.formFields.isLoading = true;
        state.formFields.error = null;
      })
      .addCase(getFormFields.fulfilled, (state, action) => {
        const result = unwrapList(action.payload);
        state.formFields.isLoading = false;
        state.formFields.data = result.data;
      })
      .addCase(getFormFields.rejected, (state, action) => {
        state.formFields.isLoading = false;
        state.formFields.error = errorMessage(action);
      })
      .addCase(saveFormFields.pending, (state) => {
        state.formFields.isMutating = true;
        state.formFields.error = null;
      })
      .addCase(saveFormFields.fulfilled, (state) => {
        state.formFields.isMutating = false;
      })
      .addCase(saveFormFields.rejected, (state, action) => {
        state.formFields.isMutating = false;
        state.formFields.error = errorMessage(action);
      })
      .addCase(deleteFormField.pending, (state) => {
        state.formFields.isMutating = true;
        state.formFields.error = null;
      })
      .addCase(deleteFormField.fulfilled, (state, action) => {
        const deletedId = action.meta.arg?.fieldId;
        state.formFields.isMutating = false;
        state.formFields.data = state.formFields.data.filter((field) => field.id !== deletedId);
      })
      .addCase(deleteFormField.rejected, (state, action) => {
        state.formFields.isMutating = false;
        state.formFields.error = errorMessage(action);
      });
      
  },
});

export const {
  clearComplaintError,
  clearSelectedComplaintResource,
  resetComplaintState,
} = complaintSlice.actions;

export default complaintSlice.reducer;
